import { trackRuntimePromise } from "./runtimeActivityService";

export type LazyStartSection = "history" | "masterData" | "allocations" | "settings" | "admin" | "visualLab" | "visionOptimizer";

const PENDING_SECTION_KEY = "materialentnahme.lazy.pending-section";
const RECOVERY_PREFIX = "materialentnahme.lazy.recovery:";

function safeSessionGet(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key: string, value: string): void {
  try { window.sessionStorage.setItem(key, value); } catch { /* optional */ }
}
function safeSessionRemove(key: string): void {
  try { window.sessionStorage.removeItem(key); } catch { /* optional */ }
}

const VALID_SECTIONS = new Set<LazyStartSection>(["history", "masterData", "allocations", "settings", "admin", "visualLab", "visionOptimizer"]);

export function pendingLazyStartSection(): LazyStartSection | null {
  const value = safeSessionGet(PENDING_SECTION_KEY);
  return value && VALID_SECTIONS.has(value as LazyStartSection) ? value as LazyStartSection : null;
}

export function rememberPendingLazyStartSection(section: LazyStartSection): void {
  safeSessionSet(PENDING_SECTION_KEY, section);
}

export function clearPendingLazyStartSection(section?: LazyStartSection): void {
  if (!section || safeSessionGet(PENDING_SECTION_KEY) === section) safeSessionRemove(PENDING_SECTION_KEY);
  if (section) safeSessionRemove(`${RECOVERY_PREFIX}${section}`);
}

function createTrackedLoader<T>(
  section: LazyStartSection,
  label: string,
  importer: () => Promise<T>,
): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => {
    if (pending) return pending;
    const recoveryKey = `${RECOVERY_PREFIX}${section}`;
    pending = trackRuntimePromise(`lazy:${section}`, label, "lazy", importer, "Code-Split wird geladen")
      .then((module) => {
        safeSessionRemove(recoveryKey);
        if (safeSessionGet(PENDING_SECTION_KEY) === section) safeSessionRemove(PENDING_SECTION_KEY);
        return module;
      })
      .catch((error) => {
        pending = null;
        const attempts = Number(safeSessionGet(recoveryKey) ?? "0");
        // Vite-Dev und ein gerade gewechselter Service Worker können einmalig einen
        // veralteten Modulgraphen liefern. Ein einziger kontrollierter Reload darf
        // das beheben, ohne den Zielbereich zu verlieren oder in einer weißen Seite
        // zu enden. Ein zweiter Fehler wird normal an die ErrorBoundary weitergereicht.
        if (attempts < 1 && navigator.onLine) {
          safeSessionSet(PENDING_SECTION_KEY, section);
          safeSessionSet(recoveryKey, String(attempts + 1));
          window.setTimeout(() => window.location.reload(), 80);
          return new Promise<T>(() => undefined);
        }
        throw error;
      });
    return pending;
  };
}

export const loadCameraCaptureDialog = () => trackRuntimePromise("lazy:camera", "Kamera-Modul", "lazy", () => import("../components/CameraCaptureDialog"), "Code-Split wird geladen");
export const loadMaterialMasterPage = createTrackedLoader("masterData", "Materialstammliste", () => import("../pages/MaterialMasterPage"));
export const loadAllocationMasterPage = createTrackedLoader("allocations", "Kontierungen", () => import("../pages/AllocationMasterPage"));
export const loadHistoryPage = createTrackedLoader("history", "Verlauf", () => import("../pages/HistoryPage"));
export const loadSettingsPage = createTrackedLoader("settings", "Einstellungen", () => import("../pages/SettingsPage"));
export const loadAdminPage = createTrackedLoader("admin", "Administration", () => import("../pages/AdminPage"));
export const loadVisualLabPage = createTrackedLoader("visualLab", "Visual Lab", () => import("../pages/VisualLabPage"));
export const loadVisionOptimizerPage = createTrackedLoader("visionOptimizer", "Vision Optimizer", () => import("../pages/VisionOptimizerPage"));
